/**
 * useSourceVisualsClaim — claims a line's source visuals and re-derives the
 * quantity it is billed for.
 *
 * The one piece of the upload that a move out of the product drawer silently
 * loses: `serializePlacement` builds each line's `inputImages` from
 * `attachments[cartItemId]` and from nothing else, and the drawer used to write
 * that slice on "Add to cart". A file that never reaches it is a file the order
 * does not carry — no error, nothing on screen, just a line with nothing to work
 * from.
 *
 * ADD-ONLY on purpose. Deletion is already complete without this hook:
 * `FileUploadSection` unclaims a filename before dropping its scope entry,
 * because `useResumedUploads` re-derives the scope FROM the attachments and would
 * otherwise restore what the client just removed. Writing an empty list back here
 * would be the same bug from the other side — during the hydrate window the scope
 * is empty while the line legitimately owns files, and an eager write would
 * delete a resumed line's own visuals.
 *
 * The quantity mirror is scoped to `EXACTLY_COUNT`, which is where the drawer
 * derived it before the move: one produced item per uploaded visual. It is what
 * makes "upload more photos" mean "buy more staged images", and it is guarded on
 * the VALUE of the filename list because the app-wide `FileAPIProvider` replaces
 * the array identity on every re-render.
 *
 * Whether files are REQUIRED is deliberately not decided here — that is a
 * catalogue fact (`inputImagePolicy`), read by `requiresSourceFiles` where the
 * catalogue is in hand.
 */

import { useEffect, useMemo } from 'react'

import { isOneItemPerVisual } from 'utils/purchaseFlowRevamp'
import type { CartItemId, InputImagePolicyV2 } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { useCanClaimSourceVisuals } from './configPanelActive'
import { claimedFilenames } from '../../store/slices/cartAttachmentsSlice'
import { isOwnedByOtherLine, uploadedFilesForProductCode, usePfV2VisualUpload } from '../../fileHub'

export interface SourceVisualsScope {
  /** Product-GROUP code the uploads are grouped under in the shared FileAPI scope. */
  scopeKey: string | null
  /** Selected variant's catalogue code — sent to the BE on the upload-URL request. */
  variantId: string | null
  /** Filenames other cart lines already own, so this line cannot re-offer them. */
  claimedByOtherLines: ReadonlySet<string>
  /** Files this line speaks for — what the order will carry. */
  claimedFilenames: string[]
  /** Whether one produced item is bought per uploaded visual. */
  isOnePerItem: boolean
}

/**
 * @param cartItemId  The line collecting the visuals.
 * @param policy      The bought variant's `inputImagePolicy`.
 */
export function useSourceVisualsClaim(
  cartItemId: CartItemId,
  policy: InputImagePolicyV2 | undefined
): SourceVisualsScope {
  const { stagingEntries } = usePfV2VisualUpload()
  // Only the visible panel claims. Two lines of one product share a scope, and
  // both claiming the same files is an infinite update loop, not a tie to break
  // afterwards — see `configPanelActive`.
  const canClaim = useCanClaimSourceVisuals()

  const shellProductId = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.shellProductId ?? null)
  const variantId = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.variantId ?? null)
  const attachments = usePfStore((s) => s.attachments)
  const setAttachments = usePfStore((s) => s.setAttachments)

  // The scope groups uploads by SHELL product, so two lines of the same product
  // share one pool; the claim is what splits it. Without this the second line
  // would re-offer — and re-order — the first line's visuals.
  const claimedByOtherLines = useMemo(
    () => claimedFilenames(attachments, cartItemId),
    [attachments, cartItemId]
  )

  const ownFiles = useMemo(
    () => uploadedFilesForProductCode((stagingEntries ?? []).filter((entry) => !isOwnedByOtherLine(entry, cartItemId)), shellProductId)
      .filter((file) => !claimedByOtherLines.has(file.gcFilename)),
    [stagingEntries, shellProductId, claimedByOtherLines, cartItemId]
  )

  const claimed = useMemo(() => attachments[cartItemId] ?? [], [attachments, cartItemId])

  // Compared as strings so the guards are over VALUES, not the array identity the
  // app-wide provider keeps replacing.
  const ownKey = ownFiles.map((file) => file.gcFilename).join(' ')
  const claimedKey = claimed.map((file) => file.gcFilename).join(' ')

  useEffect(() => {
    if (!canClaim) return
    if (ownFiles.length === 0) return
    if (ownKey === claimedKey) return

    setAttachments(cartItemId, ownFiles.map((file) => ({ kind: 'staging-visual' as const, ...file })))
  }, [canClaim, ownFiles, ownKey, claimedKey, cartItemId, setAttachments])

  useEffect(() => {
    if (!isOneItemPerVisual(policy)) return
    if (claimed.length === 0) return

    usePfStore.getState().setItemQuantity(cartItemId, claimed.length)
  }, [claimedKey, claimed.length, cartItemId, policy])

  return {
    scopeKey: shellProductId,
    variantId,
    claimedByOtherLines,
    claimedFilenames: claimed.map((file) => file.gcFilename),
    isOnePerItem: isOneItemPerVisual(policy),
  }
}
